import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import AdminNavbar from "../../Pages/Navbar/AdminNavbar";
import BaseUrl from "../../ApiServices/BaseUrl";
import "./ManageFooterContent.css";

const createEmptyProject = () => ({
  video_title: "",
  video_url: "",
  video_cta_label: "",
  video_cta_link: "",
  video_enabled: false
});

const defaultFormData = {
  section_heading: "",
  section_description: "",
  projects: [createEmptyProject(), createEmptyProject(), createEmptyProject()]
};

const getYouTubeVideoId = (url) => {
  if (!url) return "";
  const cleaned = String(url).trim();

  const embedMatch = cleaned.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/);
  if (embedMatch?.[1]) return embedMatch[1];

  const watchMatch = cleaned.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
  if (watchMatch?.[1]) return watchMatch[1];

  const shortMatch = cleaned.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
  if (shortMatch?.[1]) return shortMatch[1];

  const shortsMatch = cleaned.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/);
  if (shortsMatch?.[1]) return shortsMatch[1];

  return "";
};

const getEmbedUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : "";
};

const getThumbnailUrl = (url) => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";
};

const ManageFooterContent = () => {
  const [formData, setFormData] = useState(defaultFormData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const previewData = useMemo(
    () =>
      formData.projects.map((project) => ({
        thumbnail: getThumbnailUrl(project.video_url),
        embedUrl: getEmbedUrl(project.video_url)
      })),
    [formData.projects]
  );

  const fetchFooterContent = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BaseUrl}/api/footer-content`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch footer content");
      }

      const projects = (Array.isArray(data.projects) ? data.projects : [])
        .slice(0, 3)
        .map((project) => ({
          video_title: project.video_title || "",
          video_url: project.video_url || "",
          video_cta_label: project.video_cta_label || "",
          video_cta_link: project.video_cta_link || "",
          video_enabled: project.video_enabled === true
        }));

      while (projects.length < 3) {
        projects.push(createEmptyProject());
      }

      setFormData({
        section_heading: data.section_heading || "",
        section_description: data.section_description || "",
        projects
      });
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to fetch footer content", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterContent();
  }, []);

  const updateProject = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      projects: prev.projects.map((project, projectIndex) =>
        projectIndex === index ? { ...project, [key]: value } : project
      )
    }));
  };

  const handleSave = async () => {
    if (!formData.section_heading.trim()) {
      Swal.fire("Error", "Common footer section heading is required.", "error");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        section_heading: formData.section_heading,
        section_description: formData.section_description,
        projects: formData.projects.map((project) => ({
          details_title: "",
          details_description: "",
          details_items: [],
          details_cta_label: "",
          details_cta_link: "",
          details_enabled: false,
          video_title: project.video_title,
          video_url: project.video_url,
          video_cta_label: project.video_cta_label,
          video_cta_link: project.video_cta_link,
          video_enabled: project.video_enabled
        }))
      };

      const response = await fetch(`${BaseUrl}/api/footer-content`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to save footer content");
      }

      await fetchFooterContent();
      Swal.fire("Success", "Footer content updated successfully.", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to save footer content", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AdminNavbar />
      <div className="manage-footer-container">
        <div className="manage-footer-card">
          <div className="manage-footer-hero">
            <span className="manage-footer-eyebrow">Footer Campaign CMS</span>
            <h2>Manage up to three featured project cards in the footer.</h2>
            <p>
              Each card shows a title, a YouTube video, and a call-to-action button.
              Disable or leave a project empty to hide it from the website.
            </p>
          </div>

          {loading ? (
            <p className="text-center mb-0">Loading footer content...</p>
          ) : (
            <>
              <div className="manage-footer-section manage-footer-section-intro">
                <div className="manage-footer-section-header">
                  <h4>Common Section Content</h4>
                </div>

                <label className="manage-footer-label">Section Heading</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.section_heading}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, section_heading: e.target.value }))
                  }
                  placeholder="Explore More From Our Network"
                />

                <label className="manage-footer-label">Section Description</label>
                <textarea
                  className="form-control manage-footer-textarea"
                  value={formData.section_description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, section_description: e.target.value }))
                  }
                  placeholder="Add a short paragraph introducing these footer projects."
                />
              </div>

              {formData.projects.map((project, index) => (
                <div key={`project-${index}`} className="manage-footer-section">
                  <div className="manage-footer-section-header">
                    <div>
                      <h4>{`Project ${index + 1}`}</h4>
                      <p className="manage-footer-section-copy">
                        Title, YouTube video and CTA button shown in the footer card.
                      </p>
                    </div>
                  </div>

                  <div className="manage-footer-toggle-row">
                    <label className="manage-footer-toggle">
                      <input
                        type="checkbox"
                        checked={project.video_enabled}
                        onChange={(e) =>
                          updateProject(index, "video_enabled", e.target.checked)
                        }
                      />
                      <span>Show Project</span>
                    </label>
                  </div>

                  <label className="manage-footer-label">Title</label>
                  <input
                    type="text"
                    className="form-control"
                    value={project.video_title}
                    onChange={(e) =>
                      updateProject(index, "video_title", e.target.value)
                    }
                    placeholder={`Project ${index + 1} title`}
                  />

                  <label className="manage-footer-label">YouTube URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={project.video_url}
                    onChange={(e) =>
                      updateProject(index, "video_url", e.target.value)
                    }
                    placeholder="https://www.youtube.com/watch?v=..."
                  />

                  <div className="manage-footer-grid">
                    <div>
                      <label className="manage-footer-label">Button Label</label>
                      <input
                        type="text"
                        className="form-control"
                        value={project.video_cta_label}
                        onChange={(e) =>
                          updateProject(index, "video_cta_label", e.target.value)
                        }
                        placeholder="Learn More"
                      />
                    </div>
                    <div>
                      <label className="manage-footer-label">Button Link</label>
                      <input
                        type="text"
                        className="form-control"
                        value={project.video_cta_link}
                        onChange={(e) =>
                          updateProject(index, "video_cta_link", e.target.value)
                        }
                        placeholder="/page or https://example.com"
                      />
                    </div>
                  </div>

                  <div className="manage-footer-preview">
                    <h5>{`Project ${index + 1} Video Preview`}</h5>
                    {previewData[index]?.thumbnail ? (
                      <img
                        src={previewData[index].thumbnail}
                        alt={`Project ${index + 1} video preview`}
                        className="manage-footer-preview-image"
                      />
                    ) : (
                      <div className="manage-footer-preview-empty">
                        Enter a valid YouTube URL to preview this video.
                      </div>
                    )}
                    {previewData[index]?.embedUrl && (
                      <div className="manage-footer-preview-link">
                        Embed URL: {previewData[index].embedUrl}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <button
                type="button"
                className="save-btn manage-footer-save-btn"
                disabled={saving}
                onClick={handleSave}
              >
                {saving ? "Saving..." : "Save Footer Content"}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ManageFooterContent;
